/**
 * Admins API client
 *
 * Phase 1-6 / Plan §6.4 §6.5 / Design §5.2 E1.
 *
 * 包装 axios apiClient 调用 admin-service /admins 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。所有写动作通过 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';
import type { AdminRole } from '../contracts/roles';

export type AdminStatus = 'active' | 'disabled';

export interface AdminDto {
  id: string;
  unionId: string;
  nickname: string;
  role: AdminRole;
  status: AdminStatus;
  isLegalRole: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  createdBy: string | null;
  disabledAt: string | null;
  disabledBy: string | null;
  disableReason: string | null;
  /** 企业微信成员账号（顾问陪跑 PR⑦ 聊天侧边栏据此识别销售）；未绑定为 null */
  wecomUserId?: string | null;
}

export interface AdminDetailDto extends AdminDto {
  activeSessionCount: number;
}

export type LoginRecordStatus = 'active' | 'expired' | 'revoked';

/** 一次登录（admin_sessions 的「登录 → 续期」链归并后的一条）。 */
export interface LoginRecordDto {
  sessionId: string;
  loginAt: string;
  ip: string | null;
  userAgent: string | null;
  /** 该次登录期间 refresh 续期次数 */
  renewCount: number;
  lastRenewedAt: string;
  status: LoginRecordStatus;
  /** 仅 status='revoked' 时有值 */
  revokeReason: string | null;
  revokedAt: string | null;
}

export interface LoginHistoryResponse {
  items: LoginRecordDto[];
  /** 全部登录次数（items 受 limit 截断） */
  total: number;
  limit: number;
}

export interface ListAdminsFilter {
  status?: AdminStatus;
  role?: AdminRole;
}

export interface ListAdminsResponse {
  items: AdminDto[];
  total: number;
}

export interface CreateAdminInput {
  unionId: string;
  nickname: string;
  role: AdminRole;
  isLegalRole?: boolean;
}

export interface UpdateAdminInput {
  role?: AdminRole;
  isLegalRole?: boolean;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /admins — 列表查询（状态/角色过滤）。 */
export async function listAdmins(
  filter: ListAdminsFilter = {},
): Promise<ListAdminsResponse> {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  if (filter.role) params.role = filter.role;
  const resp = await apiClient.get<ServiceResponse<ListAdminsResponse>>('/admins', {
    params,
  });
  return resp.data.data;
}

/** GET /admins/:id — 单条详情 + activeSessionCount。 */
export async function getAdmin(id: string): Promise<AdminDetailDto> {
  const resp = await apiClient.get<ServiceResponse<AdminDetailDto>>(
    `/admins/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** GET /admins/:id/login-history — 登录记录（时间 / IP / 设备 / 结束原因），最近的在前。 */
export async function listAdminLoginHistory(
  id: string,
  limit?: number,
): Promise<LoginHistoryResponse> {
  const resp = await apiClient.get<ServiceResponse<LoginHistoryResponse>>(
    `/admins/${encodeURIComponent(id)}/login-history`,
    { params: limit ? { limit } : {} },
  );
  return resp.data.data;
}

/** POST /admins — 创建账号。 */
export async function createAdmin(input: CreateAdminInput): Promise<AdminDto> {
  const resp = await apiClient.post<ServiceResponse<AdminDto>>('/admins', input);
  return resp.data.data;
}

/** PATCH /admins/:id — 改 role / isLegalRole。 */
export async function updateAdmin(
  id: string,
  patch: UpdateAdminInput,
): Promise<AdminDto> {
  const resp = await apiClient.patch<ServiceResponse<AdminDto>>(
    `/admins/${encodeURIComponent(id)}`,
    patch,
  );
  return resp.data.data;
}

/** PATCH /admins/:id/wecom — 绑定 / 解绑（null）企业微信成员账号。 */
export async function setAdminWecom(id: string, wecomUserId: string | null): Promise<AdminDto> {
  const resp = await apiClient.patch<ServiceResponse<AdminDto>>(`/admins/${encodeURIComponent(id)}/wecom`, {
    wecomUserId,
  });
  return resp.data.data;
}

/** POST /admins/:id/disable — 停用（联动撤销会话 + 黑名单）。 */
export async function disableAdmin(id: string, reason: string): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/admins/${encodeURIComponent(id)}/disable`,
    { reason },
  );
}

/** POST /admins/:id/enable — 恢复。 */
export async function enableAdmin(id: string): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/admins/${encodeURIComponent(id)}/enable`,
    {},
  );
}

/** POST /admins/:id/revoke-session — 强制踢出会话。 */
export async function revokeAdminSession(
  id: string,
  reason?: string,
): Promise<{ revokedSessions: number }> {
  const resp = await apiClient.post<
    ServiceResponse<{ revokedSessions: number }>
  >(`/admins/${encodeURIComponent(id)}/revoke-session`, reason ? { reason } : {});
  return resp.data.data;
}
